/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

export class ReflowEvent extends Event {
  /**
   * @param {HTMLElement} lastVisibleElement - The element to move to the last visible position
   */
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section, it needs to be render-blocking to avoid layout shifts.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('defer')) {
      const deferredReflow = () => {
        // Remove attribute first to change layout before calculating the actual size
        this.removeAttribute('defer');
        this.#initialize();
      };
      const { schedule } = this;

      const requestIdleCallback =
        typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback : setTimeout;

      requestIdleCallback(() => schedule(deferredReflow));
    } else if (this.shadowRoot) {
      this.#initialize();
    } else {
      // Not all element children has been parsed yet, try again in the next tick
      // <overflow-list> is a special case as critical.js can execute before DOMContentLoaded
      setTimeout(() => {
        this.#initialize();
      }, 0);
    }
  }

  /**
   * Initialize the element
   */
  #initialize() {
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    // Add event listener for reflow requests
    this.addEventListener(
      'reflow',
      /** @param {ReflowEvent} event */ (event) => {
        this.#reflowItems(event.detail.lastVisibleElement);
      }
    );

    this.#reflowItems();
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : /** @param {FrameRequestCallback} callback */ (callback) =>
          requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @type {{width: number | null, height: number | null}}
   */
  #lastDimensions = {
    width: null,
    height: null,
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = (event) => {
    if (this.#scheduled) return;

    let width = null;
    let height = null;
    let isResize = false;

    for (const [, entry] of event.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      // There should only be one entry
      isResize = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }

    if (isResize) {
      if (!width || !height || (width === this.#lastDimensions.width && height === this.#lastDimensions.height)) {
        // Skip reflow if dimensions are 0 or the same as the last reflow
        return;
      }

      this.#lastDimensions = { width: Math.round(width), height: Math.round(height) };
    }

    this.#scheduled = true;

    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Reflow items based on available space within the list.
   * @param {HTMLElement} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (lastVisibleElement) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    // Avoid layout shifts while reflowing the list
    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};

    if (height) list.style.height = `${height}px`;
    list.style.setProperty('overflow', 'hidden');

    // Move all elements to the default slot so we can check which ones overflow
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();

    // Make sure the "More" item and placeholder are hidden
    moreSlot.hidden = true;
    placeholder.hidden = true;

    // First, check if all the items fit
    const rootRect = list.getBoundingClientRect();

    // Store the initial dimensions for comparison later
    this.#lastDimensions = { width: Math.round(rootRect.width), height: Math.round(rootRect.height) };

    const getVisibleElements = () => elements.filter((el) => el.getBoundingClientRect().top <= rootRect.top);
    let visibleElements = getVisibleElements();

    // If not all items fit or we have a lastVisibleElement, let's calculate with "More" button
    if (visibleElements.length !== elements.length || lastVisibleElement) {
      // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;
      moreSlot.style.setProperty('height', `${height}px`);

      if (lastVisibleElement) {
        lastVisibleElement.style.setProperty('order', '-1');
      }

      // Recalculate the visible elements
      visibleElements = getVisibleElements();

      // Reset the order
      moreSlot.style.removeProperty('order');
      if (lastVisibleElement) {
        lastVisibleElement.style.removeProperty('order');
      }

      // If we have a lastVisibleElement, ensure it's in the last visible position
      if (lastVisibleElement) {
        const visibleIndex = visibleElements.indexOf(lastVisibleElement);
        if (visibleIndex !== -1) {
          // Remove lastVisibleElement from its current position
          visibleElements.splice(visibleIndex, 1);
          // Add it to the end of visible elements
          visibleElements.push(lastVisibleElement);
        }
      }

      moreSlot.style.setProperty('height', 'auto');
    }

    const overflowingElements = elements.filter((element) => !visibleElements.includes(element));
    const [firstOverflowingElement] = overflowingElements;
    const hasOverflow = overflowingElements.length > 0;
    const placeholderWidth = firstOverflowingElement ? firstOverflowingElement.clientWidth : 0;

    // Move the elements to the correct slot
    for (const element of elements) {
      element.slot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
    }

    // If there are overflowing elements
    // Show more button and placeholder if needed
    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      // Set the width and height of the placeholder so the list can grow if there is space
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    list.style.removeProperty('overflow');

    // Check if the minimum-reached state should be updated
    hasOverflow && this.#updateMinimumReached(visibleElements);

    // Observe the list for changes in size
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

// Function to calculate total height of header group children
export function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }

  // If the header is transparent and has a sibling section, add the height of the header to the total height
  if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

/**
 * Initialize and maintain header height CSS variables.
 * This is critical for preventing layout shifts during page load.
 * There is a `ResizeObserver` and `MutationObserver` that kicks in post hydration in header.js
 * Note: header-group uses display: contents, so we must observe all children.
 */
function updateHeaderHeights() {
  const header = document.querySelector('header-component');

  // Early exit if no header - nothing to do
  if (!(header instanceof HTMLElement)) return;

  // Calculate initial height(s
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);

  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
}

/**
 * Updates CSS custom properties for transparent header offset calculation
 * Avoids expensive :has() selectors
 */
function updateTransparentHeaderOffset() {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  const hasHeaderSection = headerGroup?.querySelector('.header-section');
  if (!hasHeaderSection) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  /************************************************************** */
  // WATTS CUSTOM START: Check for page templates and hero sections
  const mainElement = document.querySelector('#MainContent');
  const isPageTemplate = mainElement?.getAttribute('data-template')?.startsWith('page');
  
  let shouldApplyOffset;

  if (isPageTemplate) {
    // For page templates: Check first and second <section> elements for hero
    const mainContent = document.getElementById('MainContent');
    const allDivs = mainContent?.querySelectorAll('div');
    const allSections = mainContent?.querySelectorAll('section');
    
    let hasHeroSection = false;
    
    if (allSections && allSections.length > 0) {
      // Check first section
      const firstSection = allSections[0];
      const firstDiv = allDivs?.[0];
      if (firstDiv?.classList.contains('shopify-section')) {
        firstDiv?.style.setProperty('margin-top', '0');
      }

      const isFirstSectionHero = firstSection.classList.contains('hero-wrapper');
      hasHeroSection = isFirstSectionHero;
      console.log('Has hero section:', hasHeroSection);
    }
    
    if (hasHeroSection) {

      // If page has hero section, set transparent header and add data attribute
      shouldApplyOffset = '1';
      header?.setAttribute('transparent', 'not-sticky');
      header?.classList.add('color-scheme-2');
      
      // Set up logo visibility management for pages with hero
      const headerComponent = document.querySelector('header-component');
      const originalLogo = header?.querySelector('.header-logo__image-container--original');
      const inverseLogo = header?.querySelector('.header-logo__image-container--inverse');
      
      console.log('[Watts Logo] Initial setup:', {
        headerComponent: !!headerComponent,
        originalLogo: !!originalLogo,
        inverseLogo: !!inverseLogo,
        originalLogoElement: originalLogo,
        inverseLogoElement: inverseLogo
      });
      
      if (headerComponent && (originalLogo || inverseLogo)) {
        // Function to check if menu is open
        const isMenuOpen = () => {
          const menuDrawer = document.querySelector('.menu-drawer-container');
          const isOpen = menuDrawer?.hasAttribute('open') || menuDrawer?.classList.contains('menu-open') || false;
          console.log('[Watts Logo] isMenuOpen:', isOpen, {
            hasOpenAttr: menuDrawer?.hasAttribute('open'),
            hasMenuOpenClass: menuDrawer?.classList.contains('menu-open'),
            menuDrawer: menuDrawer
          });
          return isOpen;
        };
        
        // Function to check if header background is white (when hovering over menu items)
        const isHeaderBackgroundWhite = () => {
          // Check if header has the CSS that makes background white (when hovering menu items)
          // This happens when CSS sets --header-bg-color: unset (which makes it use color-background from color-scheme-2)
          const headerRow = header?.querySelector('.header__row');
          if (!headerRow) {
            console.log('[Watts Logo] isHeaderBackgroundWhite: no headerRow found');
            return false;
          }
          
          // Check if header matches the CSS selector that makes background white
          // This is when hovering over menu items: .header:has(.mega-menu__list:hover) etc
          const hasHoveredMenu = header?.matches(':has(.mega-menu__list:hover)') ||
                                 header?.matches(':has(.menu-list__link[aria-haspopup]:hover)') ||
                                 header?.matches(':has(.menu-list__link[aria-haspopup][aria-expanded="true"])') ||
                                 header?.matches(':has(.menu-list__list-item[slot="overflow"] .menu-list__link:hover)');
          
          // Also check computed background color
          const computedBg = window.getComputedStyle(headerRow).backgroundColor;
          const bgIsNotTransparent = computedBg !== 'rgba(0, 0, 0, 0)' && computedBg !== 'transparent';
          
          console.log('[Watts Logo] isHeaderBackgroundWhite:', {
            hasHoveredMenu,
            computedBg,
            bgIsNotTransparent,
            result: hasHoveredMenu || bgIsNotTransparent
          });
          
          return hasHoveredMenu || bgIsNotTransparent;
        };
        
        // Function to update logo visibility based on sticky state and menu state
        const updateLogoVisibility = () => {
          const stickyState = headerComponent.getAttribute('data-sticky-state');
          const menuOpen = isMenuOpen();
          
          console.log('[Watts Logo] updateLogoVisibility called:', {
            stickyState,
            menuOpen,
            originalLogoExists: !!originalLogo,
            inverseLogoExists: !!inverseLogo
          });
          
          // If menu is open, always show blue logo (originalLogo)
          if (menuOpen) {
            console.log('[Watts Logo] Showing BLUE logo (menu is open)');
            if (originalLogo && originalLogo instanceof HTMLElement) {
              originalLogo.style.display = 'block';
              originalLogo.style.setProperty('display', 'block', 'important');
            }
            if (inverseLogo && inverseLogo instanceof HTMLElement) {
              inverseLogo.style.display = 'none';
              inverseLogo.style.setProperty('display', 'none', 'important');
            }
            return;
          }
          
          // Otherwise, use sticky state logic
          if (stickyState === 'inactive' || !stickyState) {
            console.log('[Watts Logo] Showing WHITE logo (transparent header)');
            // Header is at top (transparent) - show inverse logo (white)
            if (originalLogo && originalLogo instanceof HTMLElement) {
              originalLogo.style.display = 'none';
              originalLogo.style.removeProperty('display');
            }
            if (inverseLogo && inverseLogo instanceof HTMLElement) {
              inverseLogo.style.display = 'block';
              inverseLogo.style.removeProperty('display');
            }
          } else if (stickyState === 'active') {
            console.log('[Watts Logo] Showing BLUE logo (sticky header)');
            // Header is sticky - show original logo (blue)
            if (originalLogo && originalLogo instanceof HTMLElement) {
              originalLogo.style.display = 'block';
              originalLogo.style.removeProperty('display');
            }
            if (inverseLogo && inverseLogo instanceof HTMLElement) {
              inverseLogo.style.display = 'none';
              inverseLogo.style.removeProperty('display');
            }
          }
        };
        
        // Initial setup with delay to ensure data-sticky-state is set
        setTimeout(() => {
          updateLogoVisibility();
        }, 100);
        
        // Also try again after a longer delay to ensure state is correct
        setTimeout(() => {
          updateLogoVisibility();
        }, 500);
        
        // Listen for sticky state changes
        const stickyObserver = new MutationObserver(updateLogoVisibility);
        stickyObserver.observe(headerComponent, { 
          attributes: true, 
          attributeFilter: ['data-sticky-state'] 
        });
        
        // Listen for menu open/close changes
        const menuDrawer = document.querySelector('.menu-drawer-container');
        if (menuDrawer) {
          const menuObserver = new MutationObserver(updateLogoVisibility);
          menuObserver.observe(menuDrawer, {
            attributes: true,
            attributeFilter: ['class', 'open']
          });
        }
        
        // Listen for mouse events on menu items to detect when background becomes white
        const headerRow = header?.querySelector('.header__row');
        if (headerRow) {
          // Use a MutationObserver to watch for style changes on header__row
          const bgObserver = new MutationObserver(() => {
            updateLogoVisibility();
          });
          bgObserver.observe(headerRow, {
            attributes: true,
            attributeFilter: ['style', 'class']
          });
          
          // Also listen for mouseenter/mouseleave on menu items
          const menuItems = header?.querySelectorAll('.menu-list__link, .mega-menu__list');
          if (menuItems) {
            menuItems.forEach(item => {
              item.addEventListener('mouseenter', () => {
                setTimeout(updateLogoVisibility, 50);
              });
              item.addEventListener('mouseleave', () => {
                setTimeout(updateLogoVisibility, 50);
              });
            });
          }
        }
      }
      
      document.body.setAttribute('data-header-transparent', 'true');
      console.log('Page has hero section - setting transparent header and data attribute');
    }
  } else {
    // For other templates: Use original logic
    if (!header?.hasAttribute('transparent')) {
      document.body.style.setProperty('--transparent-header-offset-boolean', '0');
      return;
    }
    const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');
    shouldApplyOffset = !hasImmediateSection ? '1' : '0';
  }
  // WATTS CUSTOM END: Check for page templates and hero sections
  /************************************************************** */
  
  document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
}

export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
}

// Run both functions on page load
updateAllHeaderCustomProperties();
